import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { levelOfStructureService } from '@modules/organizationalStructure/levelOfStructure/services/levelOfStructure';
import { companiesService } from '@shared/services/companiesService';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import {
    extractErrorMessage,
    shouldBlockFetchError,
    showBlockingError,
} from '@shared/services/blockingError';

import Table from '@shared/components/Table';
import ActionButtons from '@shared/components/ActionButtons';
import { COMMON_BUTTONS } from '@config/actionButtonConfigs';
import LevelOfStructuralForm from './LevelOfStructuralForm';
import LevelOfStructureTranslation from './LevelOfStructureTranslation';
import DeleteModal from '@shared/components/DeleteModal';
import { useStatusBadge } from '@shared/components/StatusBadge';

const LEVELS_FETCH_OPTIONS = { blockingError: true };

function handleLevelsAccessError(error) {
    if (shouldBlockFetchError(error, LEVELS_FETCH_OPTIONS)) {
        showBlockingError(extractErrorMessage(error));
        return true;
    }

    return false;
}

export default function LevelsOfStructuralGroups() {
    const { t } = useTranslation();
    const adminBranchId = useSelector((state) => state.auth.adminBranchId);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [sortBy, setSortBy] = useState('planLevel');
    const [sortDir, setSortDir] = useState('asc');
    const [selectedCompany, setSelectedCompany] = useState('');
    const [searchType, setSearchType] = useState('');
    const [searchQuery, setSearchQuery] = useState({ abbreviation: '', title: '' });
    const [modalState, setModalState] = useState({ visible: false, type: null });
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalElements, setTotalElements] = useState(0);
    const [companies, setCompanies] = useState([]);
    const [companiesLoading, setCompaniesLoading] = useState(true);

    const { statusBadge } = useStatusBadge();

    // Form hook
    const formProps = useForm({
        defaultValues: {
            company: '',
            level: '',
            code: '',
            abbreviation: '',
            title: '',
            indefinite: false,
            fromDate: '',
            toDate: ''
        }
    });

    // Fetch companies
    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                setCompaniesLoading(true);
                const response = await companiesService.getAllCompanies();
                // Filter companies based on adminBranchId if available
                let filteredCompanies = response;
                if (adminBranchId && response && response.length > 0) {
                    filteredCompanies = response.filter(company => company.id === adminBranchId);
                }
                setCompanies(filteredCompanies);
                // Set default selected company to the first company in the list
                if (filteredCompanies && filteredCompanies.length > 0) {
                    setSelectedCompany(filteredCompanies[0].id);
                }
            } catch (err) {
                console.error('Error fetching companies:', err);
                if (!handleLevelsAccessError(err)) {
                    setError('Failed to load companies');
                } else {
                    setError(null);
                }
            } finally {
                setCompaniesLoading(false);
            }
        };

        if (adminBranchId !== null) {
            fetchCompanies();
        }
    }, [adminBranchId]);

    // Fetch data
    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            const searchParams = {};
            const queries = typeof searchQuery === 'object' && searchQuery !== null
                ? searchQuery
                : {};

            if (queries.abbreviation) {
                searchParams.shortDescription = queries.abbreviation;
            }
            if (queries.title) {
                searchParams.longDescription = queries.title;
            }

            const response = await levelOfStructureService.getLevels(selectedCompany, searchParams);
            const levels = Array.isArray(response) ? response : (response?.content || []);
            setData(levels);
            setTotalElements(levels.length);
        } catch (err) {
            console.error('Error fetching data:', err);
            setData([]);
            setTotalElements(0);
            if (!handleLevelsAccessError(err)) {
                setError('Failed to load data');
            } else {
                setError(null);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedCompany) {
            fetchData();
        } else {
            setData([]);
            setTotalElements(0);
        }
    }, [selectedCompany]); // Only depend on selectedCompany

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortDir('asc');
        }
    };

    const handleSelectRecord = (record) => {
        setSelectedRecord(record);
    };

    const handleSearch = () => {
        fetchData();
    };

    const handleOpenModal = (type) => {
        setModalState({ visible: true, type });
    };

    const handleCloseModal = () => {
        setModalState({ visible: false, type: null });
        setSelectedRecord(null);
        formProps.reset();
    };

    const handleConfirmDelete = async () => {
        try {
            await levelOfStructureService.deleteLevel(selectedRecord.id);
            handleCloseModal();
            fetchData();
            notifySuccess(t('common.deletedSuccessfully'));
        } catch (error) {
            console.error('Error deleting record:', error);
            const errorMessage = error.response?.data?.message || 'Failed to delete record';
            notifyError(errorMessage);
        }
    };

    const handleFormSubmit = async (formData) => {
        try {
            if (modalState.type === 'edit') {
                await levelOfStructureService.updateLevel(selectedRecord.id, formData);
                notifySuccess(t('common.updatedSuccessfully'));
            } else {
                await levelOfStructureService.createLevel(formData);
                notifySuccess(t('common.createdSuccessfully'));
            }
            handleCloseModal();
            fetchData();
        } catch (error) {
            console.error('Error saving record:', error);
            setError('Failed to save record');
            notifyError(t('common.errorMessage'));
        }
    };

    const columns = [
        { key: 'planLevel', label: t('organizationalStructure.levelsOfStructuralGroups.table.headers.level') },
        { key: 'code', label: t('organizationalStructure.levelsOfStructuralGroups.table.headers.code') },
        { key: 'shortDescription', label: t('organizationalStructure.levelsOfStructuralGroups.table.headers.abbreviation') },
        { key: 'longDescription', label: t('organizationalStructure.levelsOfStructuralGroups.table.headers.title') },
        { key: 'fromDate', label: t('organizationalStructure.levelsOfStructuralGroups.table.headers.fromDate') },
        {
            key: 'toDate',
            label: t('organizationalStructure.levelsOfStructuralGroups.table.headers.toDate'),
            render: (item) => {
                if (item.toDate === '01.01.2999' || item.toDate === '31.12.2099') {
                    return t('organizationalStructure.levelsOfStructuralGroups.form.indefinite');
                }
                return item.toDate;
            }
        },
        { key: 'companyName', label: t('organizationalStructure.levelsOfStructuralGroups.table.headers.company')},
        { key: 'translated', label: t('organizationalStructure.levelsOfStructuralGroups.table.headers.transliteration')}
    ];

    const actionButtons = (
        <ActionButtons
            buttons={[
                {
                    ...COMMON_BUTTONS.ADD,
                    onClick: () => handleOpenModal('add')
                },
                {
                    ...COMMON_BUTTONS.EDIT,
                    onClick: () => handleOpenModal('edit')
                },
                {
                    ...COMMON_BUTTONS.DELETE,
                    onClick: () => handleOpenModal('delete')
                },
                {
                    ...COMMON_BUTTONS.TRANSLATE,
                    onClick: () => handleOpenModal('translate')
                }
            ]}
            selectedRecord={selectedRecord}
        />
    );

    const searchOptions = [
        { key: 'abbreviation', label: 'abbreviation' },
        { key: 'title', label: 'title' }
    ];

    return (
        <>
            <Table
                // Data and state
                data={data}
                loading={loading}
                error={error}
                selectedData={selectedRecord?.id}
                sortBy={sortBy}
                sortDir={sortDir}
                totalElements={totalElements}
                searchQuery={searchQuery}
                searchOptions={searchOptions}
                showCompanySelect={true}
                companyOptions={companies}
                selectedCompany={selectedCompany}
                setSelectedCompany={setSelectedCompany}
                companiesLoading={companiesLoading}
                showPageSizeSelector={false}
                showPagination={false}
                // Column configuration
                columns={columns}

                // Title and actions
                title={t('organizationalStructure.levelsOfStructuralGroups.title')}
                actionButtons={actionButtons}

                // Handlers
                handleSort={handleSort}
                handleCheckboxChange={(index) => handleSelectRecord(data[index])}
                handleSearchClick={handleSearch}
                setSearchQuery={setSearchQuery}
                setSearchType={setSearchType}
                getData={fetchData}

                // Custom renderers
                renderCell={(item, column) => {
                    // If column has its own render function, use it
                    if (column.render) {
                        return column.render(item);
                    }
                    // Otherwise use statusBadge
                    return statusBadge(item, column);
                }}
            />

            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDelete}
                    selectedData={selectedRecord?.id}
                    name={selectedRecord?.longDescription}
                    entityType="record"
                />
            )}

            {modalState.visible && (modalState.type === 'add' || modalState.type === 'edit') && (
                <LevelOfStructuralForm
                    onClose={handleCloseModal}
                    onConfirm={handleFormSubmit}
                    editData={modalState.type === 'edit' ? selectedRecord : null}
                    defaultCompany={companies.find((company) => String(company.id) === String(selectedCompany)) || companies[0]}
                    formProps={formProps}
                />
            )}

            {modalState.visible && modalState.type === 'translate' && (
                <LevelOfStructureTranslation onClose={handleCloseModal} level={selectedRecord} />
            )}
        </>
    );
}
